import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { messages as staticMessages } from './constants/messages';
import { GET_CONTENT_URL } from './config';

type ContentType = typeof staticMessages;

interface ContentContextType {
  content: ContentType;
  loading: boolean;
  refreshContent: () => Promise<void>;
}

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export const ContentProvider = ({ children }: { children: ReactNode }) => {
  const [content, setContent] = useState<ContentType>(staticMessages);
  const [loading, setLoading] = useState(true);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await fetch(GET_CONTENT_URL);
      const result = await response.json();
      
      if (result.status === 'success') {
        const dbData = result.data;
        console.log('Successfully fetched content from DB:', dbData);
        // Merge fetched data with static messages as fallback
        const mergedContent = { ...staticMessages };
        
        if (dbData.common) mergedContent.common = { ...staticMessages.common, ...dbData.common };
        
        if (dbData.nav && dbData.nav.length > 0) {
            const navObj: any = {};
            dbData.nav.forEach((item: any) => {
                navObj[item.title.toLowerCase()] = item.title;
            });
            mergedContent.nav = { ...staticMessages.nav, ...navObj };
            // Also store the raw nav array for admin use
            (mergedContent as any).navArray = dbData.nav;
        }

        if (dbData.projects && dbData.projects.items) {
            mergedContent.projects = { 
                ...staticMessages.projects, 
                ...dbData.projects,
                items: dbData.projects.items.map((item: any) => ({
                    ...item,
                    image: item.image_url || item.image,
                    brochure: item.brochure_url || item.brochure
                }))
            };
        }

        if (dbData.sliders) (mergedContent as any).sliders = dbData.sliders;
        if (dbData.pages)  (mergedContent as any).pages  = dbData.pages;
        if (dbData.logo_url) (mergedContent as any).logo_url = dbData.logo_url;
        if (dbData.contact_form_fields) (mergedContent as any).contact_form_fields = dbData.contact_form_fields;
        if (dbData.contact_page) (mergedContent as any).contact_page = { ...staticMessages.contact, ...dbData.contact_page };
        if (dbData.footer_settings) (mergedContent as any).footer_settings = { ...staticMessages.footer, ...dbData.footer_settings };
        if (dbData.highlights) mergedContent.highlights = { ...staticMessages.highlights, ...dbData.highlights };
        if (dbData.amenities) mergedContent.amenities = { ...staticMessages.amenities, ...dbData.amenities };
        if (dbData.faq) mergedContent.faq = { ...staticMessages.faq, ...dbData.faq };
        
        // General content sections (about, chairman, hero)
        ['about', 'chairman', 'hero'].forEach(section => {
            if (dbData[section]) {
                mergedContent[section as keyof typeof mergedContent] = {
                    ...staticMessages[section as keyof typeof staticMessages] as any,
                    ...dbData[section]
                };
            }
        });
        
        setContent(mergedContent);
      } else {
        console.error('Backend returned error:', result.message);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  return (
    <ContentContext.Provider value={{ content, loading, refreshContent: fetchContent }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (context === undefined) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};
